import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  MessageSquare,
  Users,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Activity,
  WifiOff
} from 'lucide-react';

interface DashboardStats {
  totalMessages: number;
  totalInstances: number;
  totalContacts: number;
  messagesSent: number;
  messagesDelivered: number;
  messagesRead: number;
  activeCampaigns: number;
  messagesTrend: number;
  deliveryRate: number;
  readRate: number;
}

const MAX_RETRIES = 5;
const RETRY_DELAY = 1000;
const FETCH_TIMEOUT = 30000; // Increased to 30 seconds

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [stats, setStats] = useState<DashboardStats>({
    totalMessages: 0,
    totalInstances: 0,
    totalContacts: 0,
    messagesSent: 0,
    messagesDelivered: 0,
    messagesRead: 0,
    activeCampaigns: 0,
    messagesTrend: 0,
    deliveryRate: 0,
    readRate: 0
  });

  useEffect(() => {
    if (user?.id) {
      fetchDashboardStats();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  async function fetchDashboardStats() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      // Check if we're online
      if (!navigator.onLine) {
        throw new Error('No hay conexión a internet. Por favor, verifica tu conexión e intenta de nuevo.');
      }

      // Check if user is authenticated
      if (!user?.id) {
        navigate('/login');
        return;
      }

      setLoading(true);
      setError('');

      // Get instances count with error handling and timeout
      const { data: instancesData, error: instancesError, status: instancesStatus } = await supabase
        .from('instances')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .abortSignal(controller.signal);

      if (instancesError) {
        if (instancesStatus === 401 || instancesStatus === 403) {
          throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        }
        throw new Error(`Error al obtener instancias: ${instancesError.message}`);
      }

      if (!instancesData || instancesData.length === 0) {
        setStats({
          totalMessages: 0,
          totalInstances: 0,
          totalContacts: 0,
          messagesSent: 0,
          messagesDelivered: 0,
          messagesRead: 0,
          activeCampaigns: 0,
          messagesTrend: 0,
          deliveryRate: 0,
          readRate: 0
        });
        setLoading(false);
        return;
      }

      // Get messages stats with timeout
      const { data: messagesData, error: messagesError, status: messagesStatus } = await supabase
        .from('messages')
        .select('wa_status')
        .in('instance_id', instancesData.map(i => i.id))
        .abortSignal(controller.signal);

      if (messagesError) {
        if (messagesStatus === 401 || messagesStatus === 403) {
          throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        }
        throw new Error(`Error al obtener mensajes: ${messagesError.message}`);
      }

      // Get contacts count with timeout
      const { count: contactsCount, error: contactsError, status: contactsStatus } = await supabase
        .from('contacts')
        .select('id', { count: 'exact' })
        .in('instance_id', instancesData.map(i => i.id))
        .abortSignal(controller.signal);

      if (contactsError) {
        if (contactsStatus === 401 || contactsStatus === 403) {
          throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        }
        throw new Error(`Error al obtener contactos: ${contactsError.message}`);
      }

      // Get active campaigns count with timeout
      const { count: campaignsCount, error: campaignsError, status: campaignsStatus } = await supabase
        .from('campaigns')
        .select('id', { count: 'exact' })
        .in('instance_id', instancesData.map(i => i.id))
        .eq('status', 'running')
        .abortSignal(controller.signal);

      if (campaignsError) {
        if (campaignsStatus === 401 || campaignsStatus === 403) {
          throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        }
        throw new Error(`Error al obtener campañas: ${campaignsError.message}`);
      }

      // Calculate message statistics
      const totalMessages = messagesData?.length || 0;
      const delivered = messagesData?.filter(m => m.wa_status === 'delivered').length || 0;
      const read = messagesData?.filter(m => m.wa_status === 'read').length || 0;
      const sent = messagesData?.filter(m => m.wa_status === 'sent').length || 0;

      setStats({
        totalMessages,
        totalInstances: instancesData.length,
        totalContacts: contactsCount || 0,
        messagesSent: sent,
        messagesDelivered: delivered,
        messagesRead: read,
        activeCampaigns: campaignsCount || 0,
        messagesTrend: 0,
        deliveryRate: totalMessages > 0 ? (delivered / totalMessages) * 100 : 0,
        readRate: delivered > 0 ? (read / delivered) * 100 : 0
      });

      // Reset retry count on successful fetch
      setRetryCount(0);

    } catch (err: any) {
      console.error('Error fetching dashboard stats:', err);
      
      // Handle abort/timeout
      if (err.name === 'AbortError') {
        setError('La solicitud ha tardado demasiado. Por favor, intenta de nuevo.');
        return;
      }

      // Handle network errors
      if (!navigator.onLine || err.message?.includes('Failed to fetch')) {
        setError('Error de conexión. Verificando conexión...');
        
        // Implement retry logic with exponential backoff
        if (retryCount < MAX_RETRIES) {
          const backoffDelay = RETRY_DELAY * Math.pow(2, retryCount);
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            fetchDashboardStats();
          }, backoffDelay);
          return;
        } else {
          setError('No se pudo conectar al servidor después de varios intentos. Por favor, verifica tu conexión a internet e intenta de nuevo más tarde.');
        }
      } else if (err.message?.includes('Sesión expirada')) {
        navigate('/login');
        return;
      } else {
        // Handle other errors
        setError(err.message || 'Ha ocurrido un error al cargar los datos del tablero. Por favor, intenta de nuevo más tarde.');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }

  // Add network status listener
  useEffect(() => {
    const handleOnline = () => {
      setError('');
      setRetryCount(0); // Reset retry count when coming back online
      fetchDashboardStats();
    };

    const handleOffline = () => {
      setError('No hay conexión a internet. Reconectando cuando haya conexión disponible...');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">Tablero Principal</h1>
            <p className="mt-2 text-sm text-gray-700">
              Vista general del rendimiento de tus canales de WhatsApp Business
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              {!navigator.onLine ? (
                <WifiOff className="h-5 w-5 text-red-400" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-400" />
              )}
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
                {retryCount > 0 && retryCount < MAX_RETRIES && (
                  <p className="text-sm text-red-500 mt-1">
                    Intento {retryCount} de {MAX_RETRIES}... 
                    <span className="ml-1">
                      (Próximo intento en {(RETRY_DELAY * Math.pow(2, retryCount - 1) / 1000).toFixed(1)} segundos)
                    </span>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {/* General Stats */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MessageSquare className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total de Mensajes
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.totalMessages.toLocaleString()}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total de Contactos
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.totalContacts.toLocaleString()}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Send className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Campañas Activas
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.activeCampaigns.toLocaleString()}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Message Stats */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Mensajes Enviados
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.messagesSent.toLocaleString()}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Tasa de Entrega
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.deliveryRate.toFixed(1)}%
                      </div>
                      <div className="ml-2 flex items-baseline text-sm font-semibold">
                        {stats.messagesTrend >= 0 ? (
                          <ArrowUp className="h-4 w-4 text-green-500" />
                        ) : (
                          <ArrowDown className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Activity className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Tasa de Lectura
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        {stats.readRate.toFixed(1)}%
                      </div>
                      <div className="ml-2 flex items-baseline text-sm font-semibold">
                        {stats.messagesTrend >= 0 ? (
                          <ArrowUp className="h-4 w-4 text-green-500" />
                        ) : (
                          <ArrowDown className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Chart */}
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-gray-400" />
                Actividad Reciente
              </h3>
              <div className="mt-4 h-96 flex items-center justify-center text-gray-500">
                El gráfico de actividad estará disponible próximamente
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}